import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebaseFunctions/config";
import GlowingOrb from "./ui/GlowingOrb";

/**
 * Handles redirect logic for EO users after payment/login.
 * Checks EO_BusinessProfiles collection to determine if profile is complete.
 */
const EORedirectHandler = () => {
  const { user, initialAuthCheckDone } = useAuth();
  const [profileCheckDone, setProfileCheckDone] = useState(false);
  const [hasCompleteProfile, setHasCompleteProfile] = useState(false);
  const [firestoreSubscriptionChecked, setFirestoreSubscriptionChecked] = useState(false);
  const [, setFirestoreHasSubscription] = useState(false);

  useEffect(() => {
    const checkUserData = async () => {
      if (!user?.uid || !initialAuthCheckDone) return;

      try {
        // Check subscription status from Firestore
        const userDocRef = doc(db, "users", user.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          // Use truthy check to handle both boolean true and string "true"
          if (userData.hasActiveSubscription === true || userData.hasActiveSubscription === 'true') {
            setFirestoreHasSubscription(true);
          }
        }
        setFirestoreSubscriptionChecked(true);

        // Check EO_BusinessProfiles for complete profile
        const profileDocRef = doc(db, "EO_BusinessProfiles", user.uid);
        const profileDocSnap = await getDoc(profileDocRef);

        if (profileDocSnap.exists()) {
          const profileData = profileDocSnap.data();
          // Check if profile has required fields
          const isComplete =
            profileData.fullName?.trim() &&
            profileData.businessName?.trim();
          setHasCompleteProfile(isComplete);
        } else {
          setHasCompleteProfile(false);
        }
      } catch (error) {
        console.error("Error checking user data:", error);
        setHasCompleteProfile(false);
      }

      setProfileCheckDone(true);
    };

    checkUserData();
  }, [user?.uid, initialAuthCheckDone]);

  // Show loading while checking
  if (!initialAuthCheckDone || !profileCheckDone || !firestoreSubscriptionChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <GlowingOrb size="md" />
      </div>
    );
  }

  // Freemium model — no paywall, all EOs have access
  // If profile is not complete, redirect to complete profile
  if (!hasCompleteProfile) {
    return <Navigate to="/eo/complete-profile" replace />;
  }

  // Profile complete, go to dashboard
  return <Navigate to="/eo/dashboard" replace />;
};

export default EORedirectHandler;
